import Cookies from 'js-cookie';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import {
    disposePopover,
    disposeTooltip,
    hidePopover,
    initPopover,
    showModal,
    showPopover,
} from '../core/bootstrap-helpers.js';
import { productAvailabilityAvailable, productAvailabilityOutOfStock, productAvailabilityUnavailable, productAvailabilityBackorder, productAvailabilityDropship, productAvailabilityInStock, productAvailabilityDropshipAvailable, pricingFallbackMessage, disableOrderingOnFallback, stockDisplayCap } from '../ordering/inventory.js';
import { appendRel, escapeHtml, formatCurrencyWithSymbol, getElementByHashId, hasNonZeroAmount, safeUrl, spinnerHtml } from '../core/utilities.js';
import {
    buildInventoryBatchEntry,
    computeInventoryTotals,
    defaultBrandSettings,
    deriveStockDisplay,
    indexItemsByBatchIndex,
} from '../core/stock-display.js';
import { displayToastrAlert } from '../ui/toastr-alert.js';

dayjs.extend(relativeTime);

var _partsToValidate = {};
var firstMaybe = null;
var $vehicleYear;
var $vehicleMakeName;
var $vehicleModelName;
var $vehicleEngineMfrName;
var $vehicleEngineFamilyName;
var $vehicleEngineSizeName;
var _asyncMultiItemDataLookup = {};

export default function readyProductBrowsePage() {
  var availabilityRequest = null;
  var hasAvailability = $('[data-availability]').length > 0;
  var hasFitment = $('.load-fitment').length > 0;
  var hasRelatedParts = $('[data-equivalent-part-price]').length > 0;
  var hasPriceManager = $('[data-bs-target="#priceManagerModal"]').length > 0;
  var hasLostSales = $('[data-lost-sale]').length > 0;
  var hasVideoPopout = $('.video-popout').length > 0;
  var hasItemInOrders = $('.in-order').length > 0;
  var hasProductGalleries =
    $('.popup-gallery').length > 0 || $('.popup-gallery-mobile').length > 0;
  var hasFilterUi =
    $('.expand-collapse').length > 0 || $('.product-filters').length > 0;

  if (
    !hasAvailability &&
    !hasFitment &&
    !hasRelatedParts &&
    !hasPriceManager &&
    !hasLostSales &&
    !hasVideoPopout &&
    !hasItemInOrders &&
    !hasProductGalleries &&
    !hasFilterUi
  ) {
    return;
  }

  $vehicleYear = $('#vehicleYear');
  $vehicleMakeName = $('#vehicleMakeName');
  $vehicleModelName = $('#vehicleModelName');
  $vehicleEngineMfrName = $('#vehicleEngineMfrName');
  $vehicleEngineFamilyName = $('#vehicleEngineFamilyName');
  $vehicleEngineSizeName = $('#vehicleEngineSizeName');

  if (hasAvailability) {
    availabilityRequest = loadAvailability();
  }

  if (hasFitment) {
    loadFitmentData();
  }

  if (hasRelatedParts) {
    if (availabilityRequest && typeof availabilityRequest.always === 'function') {
      availabilityRequest.always(function () {
        loadRelatedParts();
      });
    } else {
      loadRelatedParts();
    }
  }

  if (hasProductGalleries) {
    initProductPageGallery();
  }

  if (hasPriceManager) {
    initPriceManager();
  }

  if (hasFilterUi) {
    initFilters();
  }

  if (hasLostSales) {
    initLostSales();
  }

  if (hasVideoPopout) {
    initVideoPopout();
  }

  if (hasItemInOrders) {
    initItemInOrders();
  }

  var $fallbackPage = $('#fallback-page');

  if ($fallbackPage.length) {
    $(document).on('click', '#no-fitment', function () {
      window.location.href = $fallbackPage.val();
    });
  }

  // bound unconditionally: the fitment selects need this even on pages where
  // loadAvailability() returns early or is never called
  bindDelegatedProductHandlers();

  $(document).on('click', '.product-order', function () {
    var $form = $(this).closest('form');
    var modalEl = getElementByHashId($form.attr('data-order-modal'));
    if (modalEl) {
      $(modalEl)
        .find('.multi-order-quantity')
        .val($form.find('.quantity-input').val());
    }
  });
}

export function getAsyncMultiItemKey(part) {
  return [
    part.linecode || '',
    part.stocknumber || '',
    part.aaia || '',
    part.partnumber || '',
    part.gtin || '',
  ].join('|');
}

export function getAvailabilityTargetKeyFromProduct($product) {
  if (!$product || !$product.length) {
    return '';
  }

  return [
    getAvailabilityToken($product.attr('data-whlc')),
    getAvailabilityToken($product.attr('data-whsn')),
    getAvailabilityToken($product.attr('data-aaia')).toUpperCase(),
    getAvailabilityToken($product.attr('data-pn')),
    getAvailabilityToken($product.attr('data-gtin')),
  ].join('|');
}

export function getAvailabilityToken(value) {
  if (value === null || typeof value === 'undefined') {
    return '';
  }
  return String(value).trim();
}

export function getAvailabilityItemKeyFromResponse(item) {
  if (!item) {
    return '';
  }

  return [
    getAvailabilityToken(item.linecode || item.wh_linecode),
    getAvailabilityToken(item.stocknumber || item.wh_stocknumber),
    getAvailabilityToken(
      item.aaia ||
        item.AAIABrandCode ||
        item.aaia_brand_code ||
        item.brand_code
    ).toUpperCase(),
    getAvailabilityToken(
      item.partnumber ||
        item.PartNumber ||
        item.part_number ||
        item.supplier_part_number
    ),
    getAvailabilityToken(item.gtin || item.ItemLevelGTIN || item.itemlevelgtin),
  ].join('|');
}

export function primeAsyncMultiItemLookup(parts, data) {
  if (!Array.isArray(parts) || !data) {
    return;
  }

  $.each(parts, function (idx, part) {
    if (data.hasOwnProperty(idx)) {
      _asyncMultiItemDataLookup[getAsyncMultiItemKey(part)] = data[idx];
    }
  });
}

export function getCachedAsyncMultiItem(part) {
  var key = getAsyncMultiItemKey(part);

  if (_asyncMultiItemDataLookup.hasOwnProperty(key)) {
    return _asyncMultiItemDataLookup[key];
  }

  return null;
}

export function toStockNumber(value) {
  var parsed = parseInt(value, 10);
  return isNaN(parsed) ? 0 : parsed;
}

export function getGridTotalStock(warehouseInventory, additionalInventory) {
  return toStockNumber(warehouseInventory) + toStockNumber(additionalInventory);
}

export function getGridStockDisplayText(totalInventory) {
  if (totalInventory > 0) {
    return stockDisplayCap > 0 && totalInventory > stockDisplayCap
      ? stockDisplayCap + '+'
      : totalInventory;
  }

  return productAvailabilityOutOfStock;
}

/**
 * Text for both browse stock slots from one deriveStockDisplay() view (#687).
 * The "Qty In Stock:" prefix cannot sit in front of the two-figure form.
 */
function browseStockDisplayText(view) {
  if (view.status === 'count') {
    return view.bothFigures
      ? view.stockedCount +
        ' ' +
        productAvailabilityInStock +
        ' \u00b7 ' +
        view.additionalCount +
        ' ' +
        productAvailabilityDropshipAvailable
      : view.singleCount + ' ' + view.message;
  }

  return view.message;
}

function availabilityWordings() {
  return {
    available: productAvailabilityAvailable,
    outOfStock: productAvailabilityOutOfStock,
    unavailable: productAvailabilityUnavailable,
    backorder: productAvailabilityBackorder,
    dropship: productAvailabilityDropship,
    inStock: productAvailabilityInStock,
    dropshipAvailable: productAvailabilityDropshipAvailable,
  };
}

/**
 * Reduce the stored AAM figure through the same stock-display rules as a live response.
 *
 * The stored figure is one undifferentiated pool: it cannot establish warehouse
 * orderability, backorder or manufacturer stock. Keeping those inputs neutral makes this a
 * deliberately modest first answer ("N Available" / "Out of Stock") which the richer live
 * response replaces. The decision still belongs to deriveStockDisplay(), rather than growing
 * another server/client stock ladder.
 */
export function deriveStoredInventoryDisplay(inventory, noStock, cap) {
  var parsedInventory = parseInt(inventory, 10);
  if (isNaN(parsedInventory)) {
    return null;
  }

  return deriveStockDisplay({
    item: { orderable: true },
    brandSettings: defaultBrandSettings(),
    backordering: false,
    noStock: noStock,
    cap: typeof cap === 'undefined' ? stockDisplayCap : cap,
    totals: {
      warehouseInventory: parsedInventory,
      additionalInventory: 0,
      totalInventory: parsedInventory,
    },
    wordings: availabilityWordings(),
  });
}

function paintBrowseStockSlots($wrapper, $activeCard, text, asDanger) {
  if (asDanger) {
    ownSlots($wrapper, '.inventory')
      .empty()
      .html('<span class="text-danger">' + escapeHtml(text) + '</span>');
  } else {
    ownSlots($wrapper, '.inventory').text(text);
  }
  ownSlots($activeCard, '[data-product-grid-stock]').text(text);
}

export function getProductBrowseViewMode() {
  var $scope = $('.browse-main-shell[data-browse-view]').first();
  if ($scope.length) {
    var fromData = String($scope.attr('data-browse-view') || '').toLowerCase();
    if (fromData === 'grid' || fromData === 'vertical') {
      return fromData;
    }
  }

  var $productList = $('.product-list').first();
  if (!$productList.length) {
    return 'vertical';
  }

  if ($productList.hasClass('browse-view-mode-grid')) {
    return 'grid';
  }

  if (
    $productList.hasClass('browse-view-mode-full') ||
    $productList.hasClass('browse-view-mode-vertical')
  ) {
    return 'vertical';
  }

  return 'vertical';
}

/**
 * The cost slot is server-rendered as a placeholder, so a failed or unmatched
 * availability request would otherwise leave a price sitting under the "Your
 * Cost" label that was never the account's cost. Only touches cards that were
 * waiting on a cost; cards showing a public price keep it.
 */
/**
 * The slots inside $scope that belong to $scope's OWN availability target.
 *
 * [data-availability] elements can nest: the item page's product row is one, and the
 * kit component cards in its Kits/Components tab are more, inside it. Every painter
 * below reaches its slots with .find(), which does not stop at a nested target - so
 * without this the page part's answer would be written into every component card as
 * well, and then overwritten by each card's own answer a moment later depending on
 * batch order.
 *
 * A no-op on browse, where nothing nests: with no nested target the .find() result is
 * returned untouched.
 */
function ownSlots($scope, selector) {
    var $found = $scope.find(selector);
    var $nested = $scope.find('[data-availability]');

    if (!$nested.length) {
        return $found;
    }

    return $found.not($nested.find(selector)).not($nested.filter(selector));
}

/**
 * Keep the lost-sale restock choice aligned with the best stock answer available for this
 * target. The item page can open the modal from stored stock before the live request returns,
 * so a later live answer must also reconcile an already-selected modal.
 */
export function setLostSaleStockNotificationAvailability($scope, canNotify) {
  var available = canNotify === true;
  var $buttons = ownSlots($scope, '[data-lost-sale]');

  $buttons.attr('data-lost-sale-out-of-stock', available ? '1' : '0');

  var $form = $('#lostSaleModal form');
  var activeTarget = $form.attr('data-lost-sale-target');
  var ownsActiveTarget = $buttons.filter(function () {
    return $(this).attr('data-lost-sale') === activeTarget;
  }).length;

  if (!activeTarget || !ownsActiveTarget) {
    return;
  }

  var $stockNotification = $form.find(
    '[data-lost-sale-stock-notification]'
  );
  $stockNotification.toggleClass('d-none', !available);
  if (!available) {
    $stockNotification
      .find('[name="notify_when_in_stock"]')
      .prop('checked', false);
  }
}

function clearUnloadedCost($product) {
  if ($product.attr('data-yc') != 'true') {
    return;
  }

  ownSlots($product, '.product-cost')
    .text(pricingFallbackMessage)
    .addClass('cost-text-only');
  ownSlots($product, '.cost-label, .cost-currency').remove();
}

/**
 * Delegated handlers that must exist regardless of what availability does.
 *
 * These used to live at the bottom of loadAvailability(), which returns early as soon
 * as it has inventory batches to request - so on any catalog page with stock to load
 * they never registered at all, and when fitment UI is present without availability
 * loadAvailability() is not even called. The fitment selects previously carried an
 * inline onchange, so the markup did not depend on registration; this is what replaced
 * it. Caught in review on #440.
 *
 * All three are document-delegated and the change handler is namespaced, so calling
 * this more than once is safe.
 */
function bindDelegatedProductHandlers() {
  $(document)
    .off('change.fitmentFacet')
    .on('change.fitmentFacet', 'select[data-fitment-facet]', function () {
      var $select = $(this);
      getFitmentResults(
        $select.attr('data-fitment-facet'),
        $select.val(),
        $select.attr('data-fitment-facet-label'),
        $select.find('option:selected').text(),
        $select.attr('data-fitment-refresh') === 'true'
      );
    });

  $(document)
    .off('click.locationsExpand')
    .on('click.locationsExpand', '[data-locations-expand]', function () {
      var $row = $(this);
      var status =
        $row.attr('data-locations-expand') === 'false' ? 'true' : 'false';
      $row.attr('data-locations-expand', status);
      $row.attr('aria-expanded', status === 'true' ? 'true' : 'false');
    });

  $(document)
    .off('keydown.locationsExpand')
    .on('keydown.locationsExpand', '[data-locations-expand]', function (e) {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        $(this).trigger('click');
      }
    });
}

// The availability renderer, split into four named pieces (issue #301). Each captures nothing but
// its arguments, so all four sit at module scope rather than nesting inside loadAvailability(),
// which was ~707 lines - the largest function in the codebase.
function buildPartBatch($targets) {
  var batches = [];

  $targets.each(function () {
    batches.push(
      buildInventoryBatchEntry({
        aaia: $(this).attr('data-aaia'),
        partnumber: $(this).attr('data-pn'),
        gtin: $(this).attr('data-gtin'),
        linecode: $(this).attr('data-whlc'),
        stocknumber: $(this).attr('data-whsn'),
      })
    );
  });

  return batches;
}

// the API answered with an error object: every card in flight loses its form and its cost slot
function showAvailabilityUnavailable() {
  $('.availability-batch-active').each(function (i, item) {
    var $self = $(this);
    if ($self.attr('data-stored-inventory-painted') !== '1') {
      ownSlots($self, '.loading')
        .replaceWith(
          '<span class="text-danger">Unable to Retrieve Stock</span>'
        );
    }
    ownSlots($self, '.order-form').remove();
    // Grid cards keep their cost slot outside the order form, so
    // removing the form above isn't enough to clear it.
    clearUnloadedCost($self);
  });
}

// the request itself failed - network, 500, timeout
function showAvailabilityError() {
  console.error('An error occurred while loading availability.');

  $('.availability-batch-active').each(function (i, item) {
    var $self = $(this);
    ownSlots($self, '.loading').removeClass('loading');
    ownSlots($self, '.loading-icon').remove();
    if ($self.attr('data-stored-inventory-painted') !== '1') {
      ownSlots($self, '.inventory')
        .html(
          '<span class="text-danger"><i class="fa fa-exclamation-triangle"></i> Availability Error</span>'
        );
    }
    clearUnloadedCost($self);
  });
}

// Applies one batch of results to the cards that asked for them. Everything it reads comes from
// its two arguments; each response row is matched back to the card at the batch position the
// server stamped it with, which is the position the card's entry was built at.
export function applyAvailabilityResults($targetProducts, data) {
  var itemsByBatchIndex = indexItemsByBatchIndex(data);

  var backordering = $('#backordering').length
    ? $('#backordering').val()
    : false;
  var matchedItems = 0;

  $targetProducts.each(function (batchIndex, product) {
    var item = itemsByBatchIndex[batchIndex];
    var $self = $(product);

    if (!item) {
      return;
    }

    matchedItems++;

    applyDescriptionSwap($self, item);
    applyComponentOnlyKitMarker($self, item);

    if (
      item.hasOwnProperty('account_price') &&
      item.hasOwnProperty('orderable') &&
      item.hasOwnProperty('warehouse_inventory_by_location')
    ) {
      var $activeCard = ownSlots($self, '.browse-product-card');
      if (!$activeCard.length) {
        $activeCard = $self;
      }

      var $wrapper = ownSlots($activeCard, '.loading');
      // The item page removes `.loading` after painting its stored first answer. Keep a
      // stable wrapper marker so the live warehouse response can still replace that answer.
      if (!$wrapper.length) {
        $wrapper = ownSlots($activeCard, '[data-stock-display]');
      }
      var $orderForm = ownSlots($activeCard, '.order-form');
      var $price = ownSlots($activeCard.not('.grid'), '.product-cost');
      var $gridPrice = ownSlots($activeCard.filter('.grid'), '.product-cost');
      // Grid and non-grid cards carry the same pricing data attributes,
      // but only one of the two slots exists per card.
      var $priceAttrs = $price.length ? $price : $gridPrice;
      var $quantity = ownSlots($activeCard, '.quantity-input');
      var $invInput = ownSlots($activeCard, '.inv-input');
      var $inventory = ownSlots($activeCard, '.inventory');
      var $locationsButton = ownSlots($activeCard, '[data-product-locations="button"]');
      var showPriceFallbackMessage = false;
      var removeOrderForm = false;
      var totalInventory = 0;
      var warehouseInventory = 0;
      var additionalInventory = 0;
      var aamLocations = 0;
      var manLocations = 0;
      var additionalLocations = {};
      var additionalLocations_aam = {};
      var additionalLocations_man = {};

      // The card's data-* attributes carry the same brand settings the other surfaces
      // get from catalog/ajaxGetBrand or the checkout payload - one shape for the
      // shared derivation, whatever the transport.
      var brandSettings = {
        Backorder: $self.attr('data-backorder') || '0',
        Dropship: $self.attr('data-dropship') || '0',
        DropshipZeroInventory: $self.attr('data-dropship-zeroinv') || '0',
        IncludeAAMGroupInventory: $self.attr('data-group-inventory') || '0',
        IncludeManufacturerInventory:
          $self.attr('data-manufacturer-inventory') || '0',
        LeadTimeDays: $self.attr('data-dropship-lead') || '',
        DropshipFlatRateShippingCost: $self.attr('data-dropship-cost') || '',
      };

      var totals = computeInventoryTotals(item, brandSettings, backordering);
      warehouseInventory = totals.warehouseInventory;
      additionalInventory = totals.additionalInventory;
      totalInventory = totals.totalInventory;

      // Location labels for the locations table outputLocations() renders. The
      // NUMBERS all come from computeInventoryTotals above; this loop only names
      // rows, so it keeps the card-specific gating (data-group-inventory-locations /
      // data-man-inventory-locations) and the lead-time-suffixed labels.
      if (
        backordering &&
        brandSettings['Backorder'] == '1' &&
        (brandSettings['IncludeManufacturerInventory'] == '1' ||
          brandSettings['IncludeAAMGroupInventory'] == '1')
      ) {
        if (
          $self.attr('data-group-inventory-locations') == '1' &&
          item.hasOwnProperty('aam_inventory_locations')
        ) {
          for (var key in item.aam_inventory_locations) {
            var location = item.aam_inventory_locations[key];

            if (parseInt(location.inventory) === 0) {
              continue;
            }

            var locationLabel = location.label;
            var leadTime = location.leadtime.toString();

            var aamRowLabel =
              locationLabel +
              (leadTime.length && !item.hasOwnProperty('lead_times') ? ' (' + leadTime + ')' : '');

            additionalLocations[aamRowLabel] = location.inventory;
            additionalLocations_aam[aamRowLabel] = location.inventory;
            aamLocations++;
          }
        } else if (parseInt(item.aam_inventory) > 0) {
          item.warehouse_inventory_by_location[
            'AAM Group Inventory'
          ] = item.aam_inventory;
        }

        if (
          $self.attr('data-man-inventory-locations') == '1' &&
          item.hasOwnProperty('man_inventory_locations')
        ) {
          for (var manKey in item.man_inventory_locations) {
            var manLocation = item.man_inventory_locations[manKey];

            if (parseInt(manLocation.inventory) === 0) {
              continue;
            }

            var manLabel = manLocation.label;
            var manLeadTime = manLocation.leadtime.toString();

            var manRowLabel =
              manLabel +
              (manLeadTime.length && !item.hasOwnProperty('lead_times') ? ' (' + manLeadTime + ')' : '');

            additionalLocations[manRowLabel] = manLocation.inventory;
            additionalLocations_man[manRowLabel] = manLocation.inventory;
            manLocations++;
          }
        } else if (parseInt(item.man_inventory) > 0) {
          item.warehouse_inventory_by_location[
            'Manufacturer Inventory'
          ] = item.man_inventory;
        }
      }

      $invInput.val(warehouseInventory);

      $wrapper.find('.loading-icon').remove();
      $wrapper.removeClass('loading');

      var numLocations = Object.keys(
        item.warehouse_inventory_by_location
      ).length;

      if (warehouseInventory === 0 && (aamLocations > 0 || manLocations > 0)) {
        numLocations += aamLocations;
      }

      // The stock-state ladder itself is shared now (core/stock-display.js); this
      // switch is only how a browse card dresses each verdict.
      var view = deriveStockDisplay({
        item: item,
        brandSettings: brandSettings,
        backordering: backordering,
        noStock: $inventory.hasClass('no-stock'),
        cap: stockDisplayCap,
        totals: totals,
        wordings: availabilityWordings(),
      });

      var stockText = browseStockDisplayText(view);

      switch (view.status) {
        case 'available':
          // show_stock is off: hide the numbers, not the availability.
          paintBrowseStockSlots($wrapper, $activeCard, stockText, false);
          $quantity.removeAttr('max');
          var orderModalForMax = getElementByHashId(
            $orderForm.attr('data-order-modal')
          );
          if (orderModalForMax) {
            $(orderModalForMax)
              .find('.multi-order-quantity')
              .removeAttr('max');
          }
          break;

        case 'count':
          $wrapper.find('.btn').removeAttr('disabled');
          paintBrowseStockSlots($wrapper, $activeCard, stockText, false);
          break;

        case 'dropship':
        case 'backorder':
          // The shared derivation decides WHAT to say; whether any stock exists is
          // still what decides how a card dresses it, and the two rungs that reach
          // here differ on exactly that.
          //
          // Stock somewhere but none in the warehouse: the Check Stock button has rows
          // to open, so it is enabled, and the wording renders plain - the pre-#301
          // in-stock rung.
          //
          // Nothing anywhere: the button stays as the card rendered it, which is
          // disabled (_product_card.inc emits disabled at $total_inventory == 0) - it
          // would open an empty locations table - and the wording stays red. That is
          // the pre-#301 zero rung, and it is a different answer from the one above
          // even though the derivation gives both the same status.
          if (view.inStock) {
            $wrapper.find('.btn').removeAttr('disabled');
            paintBrowseStockSlots($wrapper, $activeCard, stockText, false);
          } else {
            paintBrowseStockSlots($wrapper, $activeCard, stockText, true);
          }

          // The lead time / flat rate cost render as their own spans after the
          // locations button, as they always have here.
          if (
            view.status === 'dropship' &&
            typeof $self.attr('data-dropship-lead') !== typeof undefined
          ) {
            var dropshipText =
              '<span class="text-success dropship-lead">' +
              escapeHtml(view.leadTimeDays) +
              '</span>';

            if (view.flatRateCost.length) {
              dropshipText +=
                '<span class="dropship-cost">' +
                escapeHtml(view.flatRateCost) +
                '</span>';
            }

            $locationsButton.after(dropshipText);
          }
          break;

        case 'out-of-stock':
          removeOrderForm = true;
          paintBrowseStockSlots($wrapper, $activeCard, stockText, true);
          break;

        // 'unavailable': painted by the orderable check further down, as before -
        // it wins over whatever stock state exists.
      }

      outputLocations(
        $self,
        item,
        additionalLocations,
        additionalLocations_aam,
        additionalLocations_man,
        numLocations
      );

      $price.empty();

      var priceData = '';

      if (
        item.default_price === true &&
        $priceAttrs.attr('data-generic') == 'false'
      ) {
        if (
          typeof pricingFallbackMessage !== 'undefined' &&
          pricingFallbackMessage.length
        ) {
          priceData = pricingFallbackMessage;
          showPriceFallbackMessage = true;
        } else {
          priceData = parseFloat(
            $self.attr('data-fallback-price')
          ).toFixed(2);
        }
      } else if ($priceAttrs.attr('data-generic') == 'false') {
        priceData = parseFloat(item.account_price).toFixed(2);
      } else {
        if (item.hasOwnProperty('warehouse_list_price') && item.warehouse_list_price > 0) {
          priceData = parseFloat(item.warehouse_list_price).toFixed(2);
        } else if ($priceAttrs.attr('data-retail-price') != '') {
          priceData = parseFloat($priceAttrs.attr('data-retail-price')).toFixed(2);
        }
      }

      var orderable = item.orderable;

      // A restock subscription is meaningful only when this exact availability target has no
      // warehouse stock. Stamp the target's own lost-sale button after the live response, using
      // ownSlots() so an item page cannot stamp its nested kit/component buttons with the parent
      // part's availability.
      setLostSaleStockNotificationAvailability(
        $activeCard,
        orderable && warehouseInventory == 0
      );

      if (orderable && warehouseInventory == 0) {
          ownSlots($wrapper, '.on-order').removeClass('d-none');
          ownSlots($wrapper, '.stock-notify').removeClass('d-none');
      }

      if ($price.length > 0) {
            
          if (showPriceFallbackMessage) {
            var elementsToRemove = '.cost-label, .cost-currency';

            $price
              .text(pricingFallbackMessage)
              .addClass('cost-text-only');

            if (disableOrderingOnFallback) {
              elementsToRemove +=
                ', .btn-instant-order';
            }

            ownSlots($self, elementsToRemove).remove();
          } else if (!isNaN(priceData) && priceData > 0.0) {
            removeOrderForm = false;
            if ($self.attr('data-yc') == 'true') {
              $price.text('$' + priceData);
            }
            if (
              (backordering ||
                backordering === '1' ||
                totalInventory > 0) &&
              $orderForm.find('.product-order').length
            ) {
              $orderForm.find('.product-order').removeClass('d-none');
              $quantity.removeClass('d-none');
            }
          } else {
              orderable = false;
          }

      }

      // The grid card renders a public price server-side so the slot
      // isn't blank on first paint. Swap in the account cost here,
      // outside the block above, so grid cards can't reach the
      // availability and order-form branches they never ran before.
      if ($gridPrice.length > 0) {
        var showGridCost = $self.attr('data-yc') == 'true';

        if (showGridCost && showPriceFallbackMessage) {
          $gridPrice
            .text(pricingFallbackMessage)
            .addClass('cost-text-only');
        } else if (
          showGridCost &&
          !isNaN(priceData) &&
          priceData > 0.0
        ) {
          $gridPrice.text('$' + priceData);
        } else {
          $gridPrice.find('.loading-cost').remove();
        }
      }

      if (!orderable) {
          paintBrowseStockSlots(
            $wrapper,
            $activeCard,
            productAvailabilityUnavailable,
            true
          );
          removeOrderForm = true;
      }


      if (item.hasOwnProperty('warehouse_list_price')) {
        var listPrice = parseFloat(item.warehouse_list_price).toFixed(2);
        var priceMod = parseFloat($('#price-mod').val());
        var retailModeIsEnabled = $('#retail-mode').val() === 'true';

        if (retailModeIsEnabled) {
          if (!isNaN(priceMod)) {
            var priceIncrease =
              parseFloat(listPrice) * (priceMod / 100);
            listPrice =
              parseFloat(listPrice) + parseFloat(priceIncrease);
            listPrice = listPrice.toFixed(2);
          }

          var $retailModePrice = ownSlots($self, '[data-retail-mode-price]');

          if ($retailModePrice.attr('data-retail-price') !== '') {
            $retailModePrice.attr('data-retail-price', listPrice);
          }

          var retailModeHighestPrice = null;
          var otherPrices = $retailModePrice.attr(
            'data-other-prices'
          );

          if (
            typeof otherPrices !== 'undefined' &&
            otherPrices !== false
          ) {
            var allPrices = otherPrices.split('|');

            if (Array.isArray(allPrices)) {
              allPrices.push(listPrice);
            }

            var highestPrice = null;

            $.each(allPrices, function (i, otherPrice) {
              if (
                highestPrice === null ||
                (!isNaN(otherPrice) &&
                  parseFloat(otherPrice) > parseFloat(highestPrice))
              ) {
                highestPrice = otherPrice;
              }
            });

            retailModeHighestPrice = highestPrice;
          } else {
            retailModeHighestPrice = listPrice;
          }

          if (!isNaN(retailModeHighestPrice)) {
            retailModeHighestPrice = parseFloat(
              retailModeHighestPrice
            ).toFixed(2);
            $retailModePrice.text('$' + retailModeHighestPrice);
          }
        } else {
          ownSlots($self, '.price-retail').each(function (i, retailPrice) {
            if (listPrice > 0) {
              $(this)
                .html(
                  '<p class="product-actions-price-row" data-pm-price-retail="' +
                    listPrice +
                    '"><strong>Retail</strong> $' +
                    listPrice +
                    '</p>'
                )
                .attr('data-pm-price-retail', listPrice);
            }
          });
        }
      }
    
      if (item.hasOwnProperty('warehouse_core_price')) {
        // corePrice stays the raw signed figure the price manager reads out of
        // data-pm-price-jobber-core; only the text a buyer sees goes through
        // formatCurrencyWithSymbol(). `corePrice > 0` dropped a core-return credit
        // altogether, and a credit is a real value as of #792 - see #1176.
        var corePrice = parseFloat(item.warehouse_core_price).toFixed(2);
        var retailModeIsEnabled = $('#retail-mode').val() === 'true';
        if (!retailModeIsEnabled) {
          ownSlots($self, '.price-jobber-core').each(function (i, jcorePrice) {
            if (hasNonZeroAmount(corePrice)) {
              $(this)
                .html(
                  '<p class="product-actions-price-row" data-pm-price-jobber-core="' +
                    corePrice +
                    '"><strong>Core</strong> ' +
                    formatCurrencyWithSymbol(corePrice) +
                    '</p>'
                )
                .attr('data-pm-price-jobber-core', corePrice);
            }
          });
        }
      }

      if (!isNaN(priceData)) {
        var $priceManagerButton = ownSlots(
          $self,
          '[data-bs-target="#priceManagerModal"]'
        );

        if (
          typeof $priceManagerButton.attr('data-pm-brand') !==
            typeof undefined &&
          $priceManagerButton.attr('data-pm-brand') !== false
        ) {
          $price.attr('data-pm-price-cost', priceData);
          $priceManagerButton.prop('disabled', false);
        }
      }

      if (removeOrderForm) {
        var $lostSaleButton = ownSlots(
          $orderForm,
          '[data-bs-target="#lostSaleModal"]'
        );

        if (!$lostSaleButton.hasClass('item-lost-sale')) {
          $lostSaleButton.addClass('float-end');
        }

        $lostSaleButton.each(function (i, button) {
          var $closestOrderForm = $(this).closest('.order-form');
          $(this).insertBefore($closestOrderForm);
          $closestOrderForm.remove();
        });
      }

      if (item.hasOwnProperty('superseding')) {
        var $supersedingAlert = $self.find('.superseded');

        if (!$supersedingAlert.find('p').length) {
          var supersededString =
            '<strong>This part number has been superseded by <a href="catalog/item/' +
            item.superseding.brand.URLString +
            '/' +
            item.superseding.url_string +
            '/" style="text-decoration: underline;">' +
            item.superseding.PartNumber +
            '</a>.</strong>';

          $.each($supersedingAlert, function (i, alert) {
            if ($(this).hasClass('item')) {
              $(this).html(
                '<div class="alert alert-info">' +
                  supersededString +
                  '</div>'
              );
            } else if ($(this).hasClass('superseded-full')) {
              $(this).html(
                '<div class="col-12"><p class="text-danger text-end">' +
                  supersededString +
                  '</p></div>'
              );
            } else {
              $(this).html(
                '<p class="text-danger">' + supersededString + '</p>'
              );
            }
          });
        }
      }
    
      if (
              item.disable_instant_order_on_no_inv &&
              item.hasOwnProperty('warehouse_inventory_default_location') &&
              item.warehouse_inventory_default_location === 0
      ) {
          $self.find('.btn-instant-order')
                  .addClass('disabled')
                  .prop('disabled', true)
                  .attr('data-bs-toggle', 'tooltip')
                  .attr('title', 'Instant Ordering Unavailable: Your default warehouse has no inventory. Add to Order to select a location.');
      }
    
    } else {
      clearUnloadedCost($self);
    }
  });

  if (matchedItems == 0) {
    showAvailabilityFromMarkup(backordering);
  }
}

function paintAvailabilityFromMarkup(
  $targets,
  backordering,
  preserveCost,
  respectNoStock,
  respectCap,
  preserveOrderControls
) {
  $targets.each(function (i, item) {
    var $self = $(this);
    var inventory = parseInt($self.attr('data-inventory'));
    var $loadingElement = ownSlots($self, '.loading');
    var view = deriveStoredInventoryDisplay(
      $self.attr('data-inventory'),
      respectNoStock && ownSlots($self, '.inventory').hasClass('no-stock'),
      respectCap ? stockDisplayCap : 0
    );

    if (!view) {
      return;
    }

    var stockText = browseStockDisplayText(view);

    $loadingElement.removeClass('loading');
    $loadingElement.find('.loading-icon').remove();
    // The one .btn in .loading is the Check Stock button, which _product_card.inc
    // renders disabled at $total_inventory == 0. Enabling it here regardless opened an
    // empty locations table on the same card this function just labelled "Out of Stock" -
    // the defect the dropship/backorder rung above carries the long note about, on the
    // fallback path. NaN (no data-inventory attribute) fails this test and so keeps the
    // button as the card rendered it, which is the safe direction.
    if (inventory > 0) {
      $loadingElement.find('.btn').removeAttr('disabled');
    }
    if (view.status === 'out-of-stock') {
      ownSlots($self, '.inventory')
        .empty()
        .html('<span class="text-danger">' + escapeHtml(stockText) + '</span>');
    } else {
      ownSlots($self, '.inventory').text(stockText);
    }
    $self.attr('data-stored-inventory-painted', '1');

    if (!preserveCost) {
      clearUnloadedCost($self);
    }

    if (
      !preserveOrderControls &&
      (backordering || backordering === '1' || inventory > 0) &&
      ownSlots($self, '.product-order').length
    ) {
      ownSlots($self, '.product-order').removeClass('d-none');
    }
  });
}

// The response matched no card on the page. Falls back to the inventory each card was rendered
// with server-side, so the grid still shows a stock state rather than a stuck spinner.
function showAvailabilityFromMarkup(backordering) {
  paintAvailabilityFromMarkup(
    $('.availability-batch-active'),
    backordering,
    false,
    false,
    false,
    false
  );
}

// Only the single resolved item opts into this. Browse and specials keep their AJAX-only paint.
function paintItemInventoryFirstPaint() {
  var $targets = $('[data-item-inventory-first-paint]');
  if (!$targets.length) {
    return;
  }

  var backordering = $('#backordering').length ? $('#backordering').val() : false;
  // First paint is stock-only. In particular, do not reveal a control the server hid for
  // permissions or while live price/orderability is still unknown.
  paintAvailabilityFromMarkup($targets, backordering, true, true, true, true);
  $targets.each(function () {
    var $target = $(this);
    var view = deriveStoredInventoryDisplay(
      $target.attr('data-inventory'),
      ownSlots($target, '.inventory').hasClass('no-stock'),
      stockDisplayCap
    );

    if (view) {
      setLostSaleStockNotificationAvailability(
        $target,
        view.status === 'out-of-stock'
      );
    }
  });
}

// Swaps the PIES description for the warehouse one when the card asked for it and the response
// carried one.
/**
 * Mark a kit whose price and stock were built from its components.
 *
 * setting check_component_stock_for_unrecognized_kits. The warehouse did not recognize the kit part number, so
 * Model_warehouse_api::applyKitComponentFallback() answered from the components instead and sent
 * orderable_as_components with the row.
 *
 * THIS IS A DISPLAY MARKER AND NOT THE MECHANISM. Adding such a kit to an order adds its
 * components, and that decision is made server-side in Order::priceAndFilterItems() off the same
 * marker written by its own pricing pass - so it holds for a request that never rendered this
 * card. What the attribute buys is a hook for copy that tells the buyer why one click produced
 * several lines, and a state a test can read off the DOM rather than inferring.
 */
function applyComponentOnlyKitMarker($self, item) {
    if (!item.hasOwnProperty('orderable_as_components')) {
        return;
    }
    if (item.orderable_as_components) {
        $self.attr('data-orderable-as-components', '1');
    } else {
        $self.removeAttr('data-orderable-as-components');
    }
}

function applyDescriptionSwap($self, item) {
  var $piesDesc = $self.find('.pies-desc');
  var $whDesc = $self.find('.wh-desc');
  var $descLoader = $self.find('.desc-loader');

  if (
    item.hasOwnProperty('warehouse_description') &&
    item.warehouse_description != '' &&
    ($descLoader.hasClass('use-wh-desc') ||
      $descLoader.hasClass('no-description'))
  ) {
    $self.find('.text-wh-desc').text(item.warehouse_description);
    $piesDesc.addClass('d-none');
    $whDesc.removeClass('d-none');
  } else {
    $piesDesc.removeClass('d-none');
    $whDesc.addClass('d-none');
  }
}

function requestAvailabilityBatch(requestBatches, $targetProducts, viewAsAccount, callback) {
  if (!requestBatches.length || !$targetProducts.length) {
    if (typeof callback === 'function') {
      callback();
    }
    return null;
  }

  $targetProducts.addClass('availability-batch-active');

  return $.ajax({
    url: 'whs/get_inventory_and_pricing' + viewAsAccount,
    type: 'post',
    dataType: 'json',
    data: { parts: requestBatches },
    success: function (data) {
      primeAsyncMultiItemLookup(requestBatches, data);
      if (data.hasOwnProperty('error')) {
        showAvailabilityUnavailable();
      } else {
        applyAvailabilityResults($targetProducts, data);
      }
    },
    error: function () {
      showAvailabilityError();
    },
    complete: function () {
      // Safety net: a cost slot still showing the placeholder spinner never
      // received a price, whatever the reason (unmatched response row, card
      // skipped, no public price to fall back on). Never leave it spinning.
      $targetProducts.find('.loading-cost').remove();
      $targetProducts.removeClass('availability-batch-active');
      if (typeof callback === 'function') {
        callback();
      }
    },
  });
}

/**
 * Whether this page may ask the warehouse anything.
 *
 * layout.inc emits #static-inventory-only for a guest, because
 * Whs::get_inventory_and_pricing() refuses an unauthenticated caller. Asking anyway does not
 * degrade to the static figures - the response is not JSON, so the request fails and every card
 * gets a red "Availability Error" instead of the stock the markup already carries.
 */
export function staticInventoryOnly() {
  return $('#static-inventory-only').length > 0;
}

/**
 * Paint every card from the figures already in its markup - data-inventory, which
 * Model_pies_item::setAuxProperties() fills from the YMM part array - with no request at all.
 *
 * showAvailabilityFromMarkup() keys off .availability-batch-active, the class the request path
 * adds for the duration of a call, so the class is applied and removed around it here too.
 */
function loadAvailabilityFromMarkup($targets) {
  var backordering = $('#backordering').length ? $('#backordering').val() : false;

  $targets.addClass('availability-batch-active');
  showAvailabilityFromMarkup(backordering);
  // Same safety net the request path runs: a cost slot left spinning has no price coming.
  $targets.find('.loading-cost').remove();
  $targets.removeClass('availability-batch-active');
}

function loadAvailability() {
  paintItemInventoryFirstPaint();

  if (staticInventoryOnly()) {
    var $staticTargets = $('[data-availability]');
    if ($staticTargets.length) {
      loadAvailabilityFromMarkup($staticTargets);
    }
    return null;
  }

  if ($('[data-availability]').length) {
    var $allTargets = $('[data-availability]');
    var firstBatchSize = $allTargets.length;
    var $priorityTargets = $allTargets.slice(
      0,
      Math.min(firstBatchSize, $allTargets.length)
    );
    var $deferredTargets = $allTargets.slice(
      Math.min(firstBatchSize, $allTargets.length)
    );
    var priorityBatches = buildPartBatch($priorityTargets);
    var deferredBatches = buildPartBatch($deferredTargets);
    var $auInput = $('#au');
    var viewAsAccount = $auInput.length ? '?au=' + $auInput.val() : '';

    if (priorityBatches.length > 0) {
      return requestAvailabilityBatch(
        priorityBatches,
        $priorityTargets,
        viewAsAccount,
        function () {
          requestAvailabilityBatch(deferredBatches, $deferredTargets, viewAsAccount);
        }
      );
    }

    if (deferredBatches.length > 0) {
      return requestAvailabilityBatch(deferredBatches, $deferredTargets, viewAsAccount);
    }
  }

}

export function outputLocations(
  $element,
  itemData,
  additionalLocations,
  additionalLocations_aam,
  additionalLocations_man,
  numLocations
) {
  var warehouses = itemData.warehouse_inventory_by_location;
  var are_lead_times = false;
  var lead_times;
  if (itemData.hasOwnProperty('lead_times')) {
    lead_times = itemData.lead_times;
    are_lead_times = true;
  }
  var are_cutoffs = false;
  var next_day_cutoff;
  if (itemData.hasOwnProperty('next_day_cutoff')) {
    next_day_cutoff = itemData.next_day_cutoff;
    are_cutoffs = true;
  }
  var locationColCount =
    2 + (are_lead_times ? 2 : 0) + (are_cutoffs ? 2 : 0);
  var hasAdditionalLocations = Object.keys(additionalLocations).length;
  var displayOutput = '';
  var hasMainLocations = false;
  var datetimeStr = itemData.hasOwnProperty('datetime')
    ? ' <small class="text-muted">(Last updated ' +
      dayjs(itemData.datetime).fromNow() +
      ')</small>'
    : '';

  for (var key in warehouses) {
    displayOutput +=
      '<tr><td>' +
      escapeHtml(key) +
      '</td><td class="text-end">' +
      escapeHtml(stockDisplayCap > 0 && warehouses[key] > stockDisplayCap ? stockDisplayCap + '+' : warehouses[key]) +
      '</td>';
    if (are_lead_times) {
        displayOutput +=
        '<td></td><td>' + escapeHtml(lead_times[key] ?? '') + '</td>'
    }
    if (are_cutoffs) {
        displayOutput +=
        '<td></td><td>' + escapeHtml(next_day_cutoff[key] ?? '') + '</td>'
    }
    displayOutput += '</tr>';
    hasMainLocations = true;
  }

  if (hasAdditionalLocations) {
    var expandInitial = hasMainLocations ? 'false' : 'true';
    var ariaExpanded = hasMainLocations ? 'false' : 'true';
    displayOutput +=
      '<tr class="product-locations-display__expand" data-locations-expand="' +
      expandInitial +
      '" role="button" tabindex="0" aria-expanded="' +
      ariaExpanded +
      '"><td colspan="' +
      locationColCount +
      '"><strong>Additional Locations</strong></td></tr>';

        var sortedLocations_aam = {};

        Object.keys(additionalLocations_aam)
          .sort()
          .forEach(function (key) {
            sortedLocations_aam[key] = additionalLocations_aam[key];
          });

        for (var key in sortedLocations_aam) {
          displayOutput +=
            '<tr data-locations-additional><td>' +
            escapeHtml(key) +
            '</td><td class="text-end">' +
            escapeHtml(stockDisplayCap > 0 && sortedLocations_aam[key] > stockDisplayCap ? stockDisplayCap + '+' : sortedLocations_aam[key]) +
            '</td>';
          if (are_lead_times) {
            displayOutput +=
            '<td></td><td>' + escapeHtml(lead_times[key] ?? '') + '</td>'
          }
          if (are_cutoffs) {
            displayOutput +=
            '<td></td><td>' + escapeHtml(next_day_cutoff[key] ?? '') + '</td>'
          }
          displayOutput += '</tr>';
        }
        
        
        var sortedLocations_man = {};

        Object.keys(additionalLocations_man)
          .sort()
          .forEach(function (key) {
            sortedLocations_man[key] = additionalLocations_man[key];
          });

        for (var key in sortedLocations_man) {
          displayOutput +=
            '<tr data-locations-additional><td>' +
            escapeHtml(key) +
            '</td><td class="text-end">' +
            escapeHtml(stockDisplayCap > 0 && sortedLocations_man[key] > stockDisplayCap ? stockDisplayCap + '+' : sortedLocations_man[key]) +
            '</td>';
          if (are_lead_times) {
            displayOutput +=
            '<td></td><td>' + escapeHtml(lead_times[key] ?? '') + '</td>'
          }
          if (are_cutoffs) {
            displayOutput +=
            '<td></td><td>' + escapeHtml(next_day_cutoff[key] ?? '') + '</td>'
          }
          displayOutput += '</tr>';
        }
    
    
  }

  var thLead = are_lead_times
    ? '<th scope="col" class="product-locations-display__spacer" aria-hidden="true"></th><th scope="col">Lead Time</th>'
    : '';
  var thCutoff = are_cutoffs
    ? '<th scope="col" class="product-locations-display__spacer" aria-hidden="true"></th><th scope="col">Next-Day Cutoff</th>'
    : '';
  var tableHead =
    '<thead><tr><th scope="col">Warehouse</th><th scope="col" class="text-end">In Stock</th>' +
    thLead +
    thCutoff +
    '</tr></thead>';
  var tableOutput = displayOutput;
  displayOutput =
    '<div class="table-responsive product-locations-display__table-wrap"><table class="table table-sm align-middle product-locations-table mb-0">' +
    tableHead +
    '<tbody>' +
    displayOutput +
    '</tbody></table></div>';

  ownSlots($element, '[data-locations-timestamp]').html(datetimeStr);
  ownSlots($element, '[data-product-locations="display"]').html(displayOutput);
  ownSlots($element, '[data-product-locations="table"]').html(tableOutput);

  numLocations = parseInt(numLocations, 10);
  if (isNaN(numLocations) || numLocations < 0) {
    numLocations = 0;
  }

  $element.find('[data-product-locations="button"]').text('Check Stock');
  $element.find('[data-product-locations="button"]').each(function (i, button) {
    $(button).removeClass('d-none').show();
  });
}

export function loadFitmentData(elementsToValidate) {
  var elements =
    typeof elementsToValidate !== typeof undefined
      ? elementsToValidate
      : '.load-fitment';
  firstMaybe = null;

  var $fitmentFacetForm = $('#fitment-facet-form');

  if ($fitmentFacetForm.length) {
    $fitmentFacetForm.empty();
  }

  _partsToValidate = {};

  $(elements).each(function (i, part) {
    var $part = $(this);
    var aaia = $part.attr('data-fitment-brand');
    var pn = $part.attr('data-fitment-part');
    _partsToValidate[aaia + pn] = { aaia: aaia, pn: pn };
  });

  getFitmentResults();
}

// Flattens the `notes` payload of a YMM fitment validation result into rendered fragments.
// The API sends either an array of notes or a single string, and either shape can pack
// several notes into one entry separated by ';'. Returns escaped HTML for the <ul> on the
// fitment panel, an escaped comma-joined string for the .item-fitment-notes line, and a
// flag for whether anything survived trimming. Reads nothing but its argument - no DOM.
// The flattened array stays named `notes` on purpose: DomXssEscapingTest asserts on these
// exact escaping expressions, since there is no JavaScript test runner in this project.
function flattenFitmentNotes(rawNotes) {
  var notes = [];
  var splitNotes = [];
  var i;

  if (rawNotes instanceof Array) {
    for (var key in rawNotes) {
      splitNotes = rawNotes[key].split(';');

      for (i = 0; i < splitNotes.length; i++) {
        notes.push(splitNotes[i].trim());
      }
    }
  } else if (typeof rawNotes === 'string') {
    // A string primitive is never `instanceof String` - only `new String()` wrappers are,
    // and nothing constructs those. This branch was dead, so string-shaped notes from the
    // validation API were dropped on the floor.
    splitNotes = rawNotes.split(';');

    for (i = 0; i < splitNotes.length; i++) {
      notes.push(splitNotes[i].trim());
    }
  }

  var listItems = '';
  var stringItems = '';
  var hasContent = false;

  for (i = 0; i < notes.length; i++) {
    if (notes[i].trim().length > 0) {
      hasContent = true;
      listItems += '<li>' + escapeHtml(notes[i]) + '</li>';
      if (i > 0) {
        stringItems += ', ';
      }
      stringItems += escapeHtml(notes[i]);
    }
  }

  return {
    listItems: listItems,
    stringItems: stringItems,
    hasContent: hasContent,
  };
}

// CONCEPT: one fitment-lookup response -> the badge, the application header, the result copy and
// (for a Maybe) the facet selects. Was a single ~269-line function that interleaved interpreting
// the response with mutating the DOM, so no part of the fits / does-not-fit decision could be
// exercised without a full AJAX + DOM round trip. Split for #310; behaviour is unchanged and
// pinned by tests/js/partials/product-browse-fitment.test.js.
//
// USE: setFitmentResultForItem(data) is still the only entry point. The builders below are pure
// string functions; every DOM write happens in setFitmentResultForItem itself.

// The badge is identical for every result but its colour class. It was written out five times,
// which is what made the ladder long enough to hide the fact that resultType and the badge colour
// disagree for an unknown result - see deriveFitmentResult().
function buildFitmentResultBadge(elementKey, badgeClass, result) {
  return (
    '<span class="badge ' +
    badgeClass +
    '" data-bs-toggle="modal" data-bs-target="#fitmentModal-' +
    elementKey +
    '" style="cursor: pointer;">' +
    result +
    '</span>'
  );
}

// The "Application" / "Engine" header, plus any extra facets the shopper narrowed by. Reads the
// module-scope $vehicle* refs, which readyProductBrowsePage() assigns.
function buildFitmentApplicationInfo() {
  var output = '<div class="application-info">';
  var fitmentStr = '';
  var addResetLink = false;

  if ($vehicleYear.length) {
    output += '<p><strong>Application</strong></p>';

    fitmentStr =
      $vehicleYear.val() +
      ' / ' +
      $vehicleMakeName.val() +
      ' / ' +
      $vehicleModelName.val();

    addResetLink = true;
  } else if (
    $vehicleEngineMfrName.length &&
    ($vehicleEngineFamilyName.length || $vehicleEngineSizeName.length)
  ) {
    output += '<p><strong>Engine</strong></p>';
    fitmentStr = $vehicleEngineMfrName.val();

    fitmentStr += $vehicleEngineFamilyName.length
      ? ' / ' + $vehicleEngineFamilyName.val()
      : '';

    fitmentStr += $vehicleEngineSizeName.length
      ? ' / ' + $vehicleEngineSizeName.val()
      : '';

    addResetLink = true;
  }

  if (addResetLink) {
    output +=
      '<p>' +
      fitmentStr +
      ' <a href="/catalog/reset-vehicle/" class="reset-ymm" title="Reset"><i class="fa fa-times"></i></a></p>';
  }

  if (typeof Cookies.get('pace-fitment-application') !== typeof undefined) {
    var vehicleFacets = Cookies.get('pace-fitment-application').split(';');

    for (var i = 0; i < vehicleFacets.length - 1; i++) {
      var facetTokens = vehicleFacets[i].split('=');
      var facetNameTokens = facetTokens[0].split(':');
      var facetValueTokens = facetTokens[facetTokens.length - 1].split(':');
      var facetName = facetNameTokens[0];
      var facetLabel = facetNameTokens[facetNameTokens.length - 1];
      var facetText = facetValueTokens[facetValueTokens.length - 1];

      // Year, Make and Model are excluded because the header above already names them.
      if (
        facetLabel !== 'Year' &&
        facetLabel !== 'Make' &&
        facetLabel !== 'Model'
      ) {
        output +=
          '<p>' +
          facetLabel +
          ': ' +
          facetText +
          ' <a href="#" title="Remove Vehicle Facet" data-remove-facet="' +
          facetName +
          '"><i class="fa fa-times"></i></a></p>';
      }
    }
  }

  return output + '</div>';
}

// Bed Length arrives either already marked up with ' and " or as a bare number of inches. A value
// that is neither is unusable and drops out of the list rather than rendering as NaN.
function formatBedLengthChoice(choiceOption) {
  if (choiceOption.includes('"') || choiceOption.includes("'")) {
    return {
      text: choiceOption.replace("'", ' ft. ').replace('"', ' in. '),
      valid: true,
    };
  }

  var realFeet = choiceOption / 12;
  var feet = Math.floor(realFeet);
  var inches = Math.round((realFeet - feet) * 12);

  if (isNaN(feet) || isNaN(inches)) {
    return { text: choiceOption, valid: false };
  }

  return {
    text: feet + ' ft. ' + (inches > 0 ? inches + ' in. ' : ''),
    valid: true,
  };
}

/**
 * The two selects a Maybe renders. They are NOT the same markup and the differences are
 * load-bearing: the standalone one (which goes into #fitment-facet-form) refreshes the page on
 * change and highlights itself, the in-body one does neither. Rendering one template twice would
 * silently break the refresh.
 */
function buildFitmentFacetSelects(data, result) {
  // The facet name and label used to be baked into an inline onchange="getFitmentResults('...')"
  // string, inside a double-quoted HTML attribute - so a single quote or a paren in either value
  // broke out of the JS string into arbitrary code that ran on change. They are data attributes
  // now, read by one delegated handler (see loadAvailability()). Issue #134.
  var facetAttributes =
    ' data-fitment-facet="' +
    escapeHtml(data.name) +
    '" data-fitment-facet-label="' +
    escapeHtml(data.label) +
    '"';

  var openSelect = function (extraClass, refresh) {
    return (
      '<form><div class="mb-3"><label>' +
      escapeHtml(data.label) +
      '</label><select class="form-select' +
      extraClass +
      '" data-fitment-refresh="' +
      refresh +
      '"' +
      facetAttributes +
      '><option>Select ' +
      escapeHtml(data.label) +
      '</option>'
    );
  };

  var standalone = openSelect(
    result === 'Maybe' ? ' fitment-facet-highlight' : '',
    'true'
  );
  var inBody = openSelect('', 'false');

  var sorted = [];

  for (var choice in data.choices) {
    sorted.push([choice, data.choices[choice]]);
  }

  sorted.sort(function (a, b) {
    if (data.name == 'Year') {
      return b[1] - a[1];
    } else if (typeof a[1] === 'number') {
      return a[1] - b[1];
    } else if (typeof a[1] === 'string') {
      return a[1] > b[1] ? 1 : -1;
    }
  });

  $.each(sorted, function (i, choice) {
    var key = choice[0];
    var choiceOption = choice[1];
    var validChoiceOption = true;

    if (data.label == 'Bed Length') {
      var formatted = formatBedLengthChoice(choiceOption);
      choiceOption = formatted.text;
      validChoiceOption = formatted.valid;
    }

    if (validChoiceOption) {
      var option =
        '<option value="' +
        escapeHtml(key) +
        '">' +
        escapeHtml(choiceOption) +
        '</option>';

      standalone += option;
      inBody += option;
    }
  });

  return {
    standalone: standalone + '</select></div></form>',
    inBody: inBody + '</select></div></form>',
  };
}

/**
 * Interpretation only - what should be shown for this response. No DOM access, so the
 * fits / does-not-fit decision can be exercised on its own.
 *
 * Note resultType and the badge colour deliberately disagree for an unknown result: the button is
 * restyled btn-primary while the badge is bg-warning. That is pre-existing and pinned by test, so
 * it is reproduced rather than tidied - agreeing them would change what renders.
 */
function deriveFitmentResult(data, elementKey) {
  var result = data.result.charAt(0).toUpperCase() + data.result.slice(1);
  var view = {
    result: result,
    resultType: '',
    resultLabel: '',
    bodyHtml: '',
    // Always defined, so a result with no notes clears whatever the previous one left in
    // .item-fitment-notes rather than leaving it on screen against the wrong part.
    notes: '',
    revalidate: false,
    facetSelects: null,
  };

  if (result == 'Yes') {
    view.resultType = 'success';
    view.resultLabel = buildFitmentResultBadge(elementKey, 'bg-success', result);
    view.bodyHtml +=
      '<p class="text-success fitment-info-result-label"><strong><i class="fa fa-check"></i> This product will fit your application.</strong></p>';

    if (data.hasOwnProperty('notes')) {
      var flattenedNotes = flattenFitmentNotes(data.notes);

      if (flattenedNotes.hasContent) {
        view.bodyHtml +=
          '<p><strong>Notes:</strong></p><ul>' +
          flattenedNotes.listItems +
          '</ul>';
        view.notes = 'Fitment Notes: ' + flattenedNotes.stringItems;
      }
    }
  } else if (result == 'Universal') {
    view.resultType = 'success';
    view.resultLabel = buildFitmentResultBadge(elementKey, 'bg-success', result);
    view.bodyHtml +=
      '<p class="text-success fitment-info-result-label"><strong><i class="fa fa-check"></i> This product is universal and will fit your application.</strong></p>';
  } else if (result == 'No') {
    view.resultType = 'danger';
    view.resultLabel = buildFitmentResultBadge(elementKey, 'bg-danger', result);
    view.bodyHtml +=
      '<p class="text-danger fitment-info-result-label"><strong><i class="fa fa-ban"></i> This product will not fit your application.</strong></p><p><button type="button" role="button" id="no-fitment" class="btn btn-danger">Find Products That Do Fit</button></p>';
  } else if (result == 'Maybe') {
    view.resultType = 'warning';
    view.resultLabel = buildFitmentResultBadge(
      elementKey,
      'bg-warning text-dark',
      result
    );
    view.revalidate = true;

    if (data.hasOwnProperty('choices') && typeof data.name !== typeof undefined) {
      view.facetSelects = buildFitmentFacetSelects(data, result);
      view.bodyHtml += view.facetSelects.inBody;
    }
  } else {
    view.resultType = 'primary';
    view.resultLabel = buildFitmentResultBadge(
      elementKey,
      'bg-warning text-dark',
      result
    );
    view.bodyHtml +=
      '<p class="text-warning fitment-info-result-label"><strong><i class="fa fa-question-circle"></i> It is unknown whether this product will fit your application or not.</strong></p>';
  }

  return view;
}

function updateGridFitment($scope, result) {
  $scope.find('.browse-grid-fitment').each(function () {
    var $button = $(this);
    var state = result.toLowerCase();
    var labels = {
      yes: 'Fits your ' + ($button.attr('data-fitment-subject') || 'vehicle'),
      no: 'Does not fit',
      universal: 'Universal fit',
      maybe: 'Check fitment details',
      unknown: 'Fitment unknown',
      error: 'Fitment unavailable',
    };
    $button.attr('data-fitment-state', state).text(labels[state] || labels.unknown);
  });
}

export function setFitmentResultForItem(data) {
  var elementKey = data.BrandCode + data.Part;
  var $item = $(
    '[data-fitment-brand="' +
      data.BrandCode +
      '"][data-fitment-part="' +
      data.Part +
      '"]'
  );
  var $product = $item.closest('.product-group');
  var $fitmentInfo = $product.find('.fitment-info');

  var view = deriveFitmentResult(data, elementKey);

  // Only the first Maybe of a batch claims the standalone facet form; firstMaybe is reset by
  // loadFitmentData() at the start of each run.
  if (view.facetSelects !== null && firstMaybe === null) {
    firstMaybe = data;

    if ($('#fitment-facet-form').length) {
      $('#fitment-facet-form').html(view.facetSelects.standalone);
    }
  }

  if (view.revalidate) {
    $item.attr('data-revalidate', 'true');
  } else {
    $item.removeAttr('data-revalidate');
  }

  if ($item.hasClass('fitment-button')) {
    $item
      .removeClass('btn-secondary btn-warning btn-success btn-danger')
      .addClass('btn-' + view.resultType)
      .text('Fitment: ' + view.result);
  }

  $fitmentInfo.html(buildFitmentApplicationInfo() + view.bodyHtml);
  $('.item-fitment-notes').html(view.notes);
  $product.find('.fitment-label span').replaceWith(view.resultLabel);
  updateGridFitment($product, view.result);
}

export function getFitmentResults(
  setFacetName,
  setFacetOption,
  setFacetLabel,
  setFacetText,
  refreshPage
) {
  setFacetName = setFacetName || null;
  setFacetOption = setFacetOption || null;
  setFacetLabel = setFacetLabel || null;
  setFacetText = setFacetText || null;
  refreshPage = refreshPage || false;

  var parts = [];

  for (var i in _partsToValidate) {
    var part = _partsToValidate[i];

    parts.push({
      aaia: part.aaia,
      pn: part.pn,
    });
  }

  if (parts.length === 0) {
    return;
  }

  var hasFacetUpdate =
    setFacetName !== null &&
    setFacetOption !== null &&
    setFacetLabel !== null &&
    setFacetText !== null;
  var hasVehicleContext =
    ($vehicleYear.length &&
      $vehicleMakeName.length &&
      $vehicleModelName.length &&
      $vehicleYear.val() &&
      $vehicleMakeName.val() &&
      $vehicleModelName.val()) ||
    ($vehicleEngineMfrName.length &&
      (($vehicleEngineFamilyName.length && $vehicleEngineFamilyName.val()) ||
        ($vehicleEngineSizeName.length && $vehicleEngineSizeName.val())));

  if (!hasFacetUpdate && !hasVehicleContext) {
    return;
  }

  var ajaxOptions = {
    url: 'ymm/validate_fitment',
    type: 'post',
    data: {
      vehicle: {},
      parts: parts,
    },
    dataType: 'json',
    error: function () {
      var resultLabel = '<span class="badge bg-danger">Error</span>';

      $('[data-revalidate]').removeAttr('data-revalidate');

      $('.fitment-button')
        .removeClass('btn-secondary btn-warning btn-success btn-danger')
        .addClass('btn-danger')
        .text('Fitment: ' + resultLabel);

      $('.fitment-label span').replaceWith(resultLabel);
      updateGridFitment($(document), 'error');
    },
    success: function (data) {
      if (refreshPage || data === null) {
        return;
      }

      for (var i in data) {
        setFitmentResultForItem(data[i]);
      }
    },
  };

  if (hasFacetUpdate) {
    var applicationOutput = '';
    var updatedApplicationOutput = '';
    var currentCookie = '';
    var fitmentStr = '';
    var addResetLink = false;

    if ($vehicleYear.length) {
      updatedApplicationOutput += '<p><strong>Application</strong></p>';
      fitmentStr =
        $vehicleYear.val() +
        ' / ' +
        $('#vehicleMakeName').val() +
        ' / ' +
        $('#vehicleModelName').val();
      addResetLink = true;
    } else if (
      $vehicleEngineFamilyName.length ||
      $vehicleEngineSizeName.length
    ) {
      updatedApplicationOutput += '<p><strong>Engine</strong></p>';
      fitmentStr = $vehicleEngineMfrName.val();
      fitmentStr += $vehicleEngineFamilyName.length
        ? ' / ' + $vehicleEngineFamilyName.val()
        : '';
      fitmentStr += $vehicleEngineSizeName.length
        ? ' / ' + $vehicleEngineSizeName.val()
        : '';
      addResetLink = true;
    }

    if (addResetLink) {
      updatedApplicationOutput +=
        '<p>' +
        fitmentStr +
        ' <a href="/catalog/reset-vehicle/" class="reset-ymm" title="Reset"><i class="fa fa-times"></i></a></p>';
    }

    if (typeof Cookies.get('pace-fitment-application') !== typeof undefined) {
      currentCookie += Cookies.get('pace-fitment-application');
    }

    Cookies.set(
      'pace-fitment-application',
      currentCookie +
        setFacetName +
        ':' +
        setFacetLabel +
        '=' +
        setFacetOption +
        ':' +
        setFacetText +
        ';',
      { expires: 365, path: "/;SameSite=None", secure: true}
    );

    var vehicleFacets = Cookies.get('pace-fitment-application').split(';');

    for (var i = 0; i < vehicleFacets.length - 1; i++) {
      var facetTokens = vehicleFacets[i].split('=');
      var facetNameTokens = facetTokens[0].split(':');
      var facetValueTokens = facetTokens[facetTokens.length - 1].split(':');
      var facetName = facetNameTokens[0];
      var facetLabel = facetNameTokens[facetNameTokens.length - 1];
      var facetOption = facetValueTokens[0];
      var facetText = facetValueTokens[facetValueTokens.length - 1];

      if (
        typeof facetName !== typeof undefined &&
        typeof facetOption !== typeof undefined
      ) {
        ajaxOptions.data.vehicle[facetName] = facetOption;
        ajaxOptions.data.vehicle[facetName + 'Label'] = facetLabel;
        ajaxOptions.data.vehicle[facetName + 'Value'] = facetText;

        if (facetName == 'MakeID') {
          ajaxOptions.data.vehicle['MakeName'] = facetText;
        } else if (facetName == 'BaseVehicleID') {
          ajaxOptions.data.vehicle['ModelName'] = facetText;
        }

        if (
          facetLabel !== 'Year' &&
          facetLabel !== 'Make' &&
          facetLabel !== 'Model'
        ) {
          var facetOutput =
            '<p>' +
            facetLabel +
            ': ' +
            facetText +
            ' <a href="#" title="Remove Vehicle Facet" data-remove-facet="' +
            facetName +
            '"><i class="fa fa-times"></i></a></p>';
          applicationOutput += facetOutput;
          updatedApplicationOutput += facetOutput;
        }
      }
    }

    if (!refreshPage) {
      $('#fitment-application-facets').html(applicationOutput);
    }

    ajaxOptions.complete = function () {
      if (refreshPage) {
        window.location.href = window.location.href;
      } else {
        if (Object.keys(ajaxOptions.data.vehicle).length === 0) {
          loadFitmentData('[data-revalidate="true"]');
        }

        $('.application-info').each(function (i, item) {
          $(this).html(updatedApplicationOutput);
        });
      }
    };
  }

  $.ajax(ajaxOptions);
}

if (typeof window !== 'undefined') {
  window.getFitmentResults = getFitmentResults;
}

function loadRelatedParts() {
  var set = {};
  var parts = {};
  var uncachedParts = [];
  var index = 0;
  var $auInput = $('#au');
  var viewAsAccount = $auInput.length ? '?au=' + $auInput.val() : '';

  function updateEquivalentPartPrice(part, item) {
    var $element = $(
      '[data-equivalent-part-price][data-whlc="' +
        part.linecode +
        '"][data-whsn="' +
        part.stocknumber +
        '"]'
    );
    var result = parseFloat(item.account_price).toFixed(2);

    if ($element.attr('data-generic') == 'false' && result > 0) {
      $element.text('$' + result);
    } else {
      $element.text('');
    }
  }

  $('[data-equivalent-part-price]').each(function (i, item) {
    var whlc = $(item).attr('data-whlc');
    var whsn = $(item).attr('data-whsn');
    var aaia = $(item).attr('data-aaia');
    var pn = $(item).attr('data-pn');
    var gtin = $(item).attr('data-gtin');
    var key = whlc + '|' + whsn;

    if ($(item).attr('data-yc') == 'true' && !set.hasOwnProperty(key)) {
      var part = {
        aaia: aaia,
        partnumber: pn,
        gtin: gtin,
        linecode: whlc,
        stocknumber: whsn,
      };

      set[key] = part;
      parts[index] = part;
      var cachedPartData = getCachedAsyncMultiItem(part);
      if (cachedPartData !== null) {
        updateEquivalentPartPrice(part, cachedPartData);
      } else {
        uncachedParts.push(part);
      }
      index++;
    }
  });

  if (uncachedParts.length > 0) {
    $.ajax({
      url: 'whs/get_inventory_and_pricing' + viewAsAccount,
      type: 'post',
      dataType: 'json',
      data: {
        parts: uncachedParts,
      },
      success: function (data) {
        primeAsyncMultiItemLookup(uncachedParts, data);
        $.each(data, function (i, item) {
          if (!uncachedParts.hasOwnProperty(i)) {
            return true;
          }
          updateEquivalentPartPrice(uncachedParts[i], item);
        });
      },
    });
  }

  $('#show-more-equivalent-parts').click(function (e) {
    e.preventDefault();
    $(this).prev('#more-equivalent-parts').removeClass('d-none');
    $(this).remove();
  });
}

function initProductPageGallery() {
  if ($('.popup-gallery').length || $('.popup-gallery-mobile').length) {
    var galleries = ['.popup-gallery', '.popup-gallery-mobile'];

    for (var i = 0; i < galleries.length; i++) {
      if ($(galleries[i]).length) {
        $(galleries[i]).magnificPopup({
          delegate: 'a.thumbnail',
          type: 'image',
          tLoading: 'Loading image #%curr%...',
          mainClass: 'mfp-img-mobile',
          gallery: {
            enabled: true,
            navigateByImgClick: true,
            preload: [0, 1],
          },
          image: {
            tError:
              '<a href="%url%">The image #%curr%</a> could not be loaded.',
            titleSrc: function (item) {
              return item.el.attr('title');
            },
        },
      });
    }
  }
  return null;
}
}

function initPriceManager() {
  $(document).on('click', '[data-bs-target="#priceManagerModal"]', function () {
    var $priceManager = $('#priceManager');
    var $product = $(this).closest('.product');
    var priceCost = $product
      .find('[data-pm-price-cost]')
      .attr('data-pm-price-cost');
    var priceJobber = $product
      .find('[data-pm-price-jobber]')
      .attr('data-pm-price-jobber');
    var priceMAP = $product
      .find('[data-pm-price-map]')
      .attr('data-pm-price-map');
    var priceRetail = $product
      .find('[data-pm-price-retail]')
      .attr('data-pm-price-retail');
    var exchangeRate = $('#exchange-rate').length
      ? $('#exchange-rate').val()
      : '';
    var url =
      'catalog/ajaxLoadPriceManager?brand=' +
      $(this).attr('data-pm-brand') +
      '&part=' +
      $(this).attr('data-pm-part');

    if (typeof priceCost !== typeof undefined && priceCost !== false) {
      url += '&cost=' + priceCost;
    }

    if (typeof priceJobber !== typeof undefined && priceJobber !== false) {
      url += '&jobber=' + priceJobber;
    }

    if (typeof priceMAP !== typeof undefined && priceMAP !== false) {
      url += '&map=' + priceMAP;
    }

    if (typeof priceRetail !== typeof undefined && priceRetail !== false) {
      url += '&ret=' + priceRetail;
    }

    if (exchangeRate !== '') {
      url += '&exchange_rate=' + exchangeRate;
    }

    // The endpoint answers JSON now rather than a bare HTML document (#922). It used to echo
    // the Price Manager vendor's response body straight through, which made this URL a way to
    // serve arbitrary third-party markup from this origin to anyone who could be sent the
    // link. dataType is explicit so a non-JSON body is an error here instead of being guessed
    // at and injected.
    $.ajax({
      url: url,
      dataType: 'json',
      beforeSend: function () {
        $priceManager.html(
          '<p class="text-center"><i class="fa fa-circle-o-notch fa-spin fa-fw"></i> Loading...</p>'
        );
      },
      success: function (response) {
        var markup =
          response && typeof response.html === 'string' ? response.html : '';

        if (markup === '') {
          $priceManager.html(
            '<p class="text-center">Pricing is not available for this part.</p>'
          );
          return;
        }

        $priceManager.html(markup);
      },
      error: function () {
        // A failure used to leave the spinner running forever. The 403 from the new
        // authentication guard is the case that made that visible.
        $priceManager.html(
          '<p class="text-center">Pricing could not be loaded.</p>'
        );
      },
    });
  });
}

function initFilters() {
  var $productFilters = $('.product-filters');

  if ($(window).width() < 768 && $productFilters.find('.collapse').length) {
    $productFilters.find('.collapse').removeClass('show');

    $productFilters
      .find('.expand-collapse')
      .attr('aria-expanded', 'false')
      .find('i')
      .removeClass('fa-minus fa-plus-square-o fa-minus-square-o')
      .addClass('fa-plus');

    $productFilters.removeClass('pre-load');
  }
}

export function initLostSales() {
  $(document).on('click', '[data-lost-sale]', function () {
    var $button = $(this);
    var $form = $('#lostSaleModal form');
    var url = '/order/record_lost_sale/' + $button.attr('data-lost-sale');
    url += $('#vaa').length ? '?au=' + $('#vaa').val() : '';
    $form
      .attr('action', url)
      .attr('data-lost-sale-target', $button.attr('data-lost-sale'));
    var $stockNotification = $form.find(
      '[data-lost-sale-stock-notification]'
    );
    $stockNotification
      .toggleClass(
        'd-none',
        $button.attr('data-lost-sale-out-of-stock') !== '1'
      )
      .find('[name="notify_when_in_stock"]')
      .prop('checked', false);
  });

  $(document).on('change', '#LostSaleReasonRowID', function () {
    var $select = $(this);
    var $form = $select.closest('form');
    var reason = $select.find('option:selected').text().trim();
    var isOther = reason === 'Other';
    var isPriceNotCompetitive = reason === 'Price Not Competitive';
    var $noteRow = $form.find('[data-lost-sale-note]');
    var $note = $noteRow.find('#Note');

    $note.val('');
    $noteRow.toggleClass('d-none', !isOther && !isPriceNotCompetitive);
    $noteRow
      .find('[data-lost-sale-note-label]')
      .text(isPriceNotCompetitive ? 'Competitor / Price' : 'Note');
    $note.attr(
      'placeholder',
      isPriceNotCompetitive ? 'Competitor name and price' : 'Note'
    );
    $noteRow
      .find('[data-lost-sale-competitor-help]')
      .toggleClass('d-none', !isPriceNotCompetitive);
  });
}

function initVideoPopout() {
  $(document).on('click', '.video-popout', function (e) {
    e.preventDefault();
    var $self = $(this);
    var $modal = $('#videoModal');
    var videoURL = $self.attr('href');
    var embedURL = '';
    var modalTitle = 'Product Video';

    if (videoURL.indexOf('watch?v=') !== -1) {
      var urlTokens = videoURL.split('=');
      embedURL =
        'https://www.youtube.com/embed/' +
        encodeURIComponent(urlTokens[urlTokens.length - 1]);
    } else {
      // anything that is not a youtube watch link goes in verbatim, so it has to be
      // checked: javascript: and data: URLs are both valid in an iframe src
      embedURL = safeUrl(videoURL);
    }

    if (typeof $self.attr('title') !== typeof undefined) {
      modalTitle = $self.attr('title');
    }

  if (embedURL.length > 0) {
      $modal.find('#videoModalLabel').text(modalTitle);
      $modal
        .find('.modal-body')
        .html(
          '<div class="ratio ratio-16x9"><iframe title="' +
            escapeHtml(modalTitle) +
            '" src="' +
            escapeHtml(appendRel(embedURL)) +
            '&autoplay=1"></iframe></div>'
        );
      showModal($modal);
    }
  });

  $('#videoModal').on('hide.bs.modal', function (e) {
    $(this).find('#videoModalLabel').empty();
    $(this).find('.modal-body').empty();
  });
}

function initItemInOrders() {
  var viewAsAccount = $('#vaa').length ? '?au=' + $('#vaa').val() : '';

  // Builds the badge's popover around a fixed piece of content.
  //
  // The loaded order list cannot be swapped into the instance that is already
  // on screen, so every content change is a fresh instance. Two separate
  // Bootstrap 5 behaviours force that:
  //
  //   - initPopover() goes through Popover.getOrCreateInstance(), which
  //     ignores the options argument when an instance already exists, so
  //     re-initialising with new `content` is silently a no-op and the
  //     spinner stays put;
  //   - Tooltip.show() queues a completion callback that reads `_isHovered`
  //     as a mouse-leave once it has been set, so calling show() a second
  //     time on a trigger:'manual' popover (directly, or via setContent(),
  //     which re-enters show()) makes it hide itself a moment after opening.
  function buildInOrderPopover($badge, content) {
    disposePopover($badge);
    disposeTooltip($badge);

    initPopover($badge, {
      html: true,
      trigger: 'manual',
      placement: 'bottom',
      content: content,
      title: $badge.attr('data-popover-title'),
    });

    // Constructing a popover moves `title` onto data-bs-original-title, so the
    // plain browser tooltip has to be re-applied after every rebuild.
    if ($badge.attr('data-tooltip-title')) {
      $badge.attr('title', $badge.attr('data-tooltip-title'));
    }
  }

  // Rebuild, then re-open only if the badge is still toggled on - the user may
  // have clicked it shut while the request was in flight.
  function replaceInOrderPopover($badge, content) {
    buildInOrderPopover($badge, content);

    if ($badge.attr('data-popover-enabled') == 'true') {
      showPopover($badge);
    }
  }

  $('.in-order').each(function () {
    buildInOrderPopover($(this), spinnerHtml('Loading...'));
  });

  $(document).off('click.in-orders', '.in-order');
  $(document).on('click.in-orders', '.in-order', function (e) {
    var $self = $(this);

    if ($self.attr('data-popover-enabled') == 'true') {
      hidePopover($self);
      $self.removeAttr('data-popover-enabled');
      return;
    }

    $self.attr('data-popover-enabled', 'true');
    showPopover($self);

    if (
      $self.attr('data-popover-loaded') == 'true' ||
      $self.attr('data-popover-loading') == 'true'
    ) {
      return;
    }

    $self.attr('data-popover-loading', 'true');

    $.ajax({
      url: 'order/get_orders_for_item' + viewAsAccount,
      type: 'post',
      dataType: 'json',
      data: {
        aaia: $self.attr('data-aaia'),
        pn: $self.attr('data-pn'),
        set: $self.attr('data-set'),
      },
      success: function (response) {
        var isCurrent = $self.attr('data-set') == 'current';
        var content = '';

        for (var orderId in response) {
          // A submitted order is only viewable through the printable history
          // page - Order has no for_print(), so the old /order/for-print/<id>
          // was a 404 on every past-order link.
          var href =
            (isCurrent ? 'order/view/' : 'history/for-print/') + orderId;

          content +=
            '<a class="link-order" href="' +
            href +
            viewAsAccount +
            '">' +
            escapeHtml(response[orderId].OrderName) +
            '</a>';
        }

        if (content === '') {
          content = '<span class="text-muted">No orders found.</span>';
        }

        $self.attr('data-popover-loaded', 'true');
        replaceInOrderPopover($self, content);
      },
      error: function () {
        console.error('Failed to load ' + $self.attr('data-set') + ' orders');
        // left unloaded on purpose, so the next click retries
        replaceInOrderPopover(
          $self,
          '<span class="text-danger">Could not load orders.</span>'
        );
      },
      complete: function () {
        $self.removeAttr('data-popover-loading');
      },
    });
  });
}

/**
 * Register or drop a stock notification for one part.
 *
 * The success handler used to be empty, so a shopper who ticked the box got no confirmation and -
 * because the caller was an inline onclick invoking a bare `stockNotify` that had never been on
 * window - no request either (#847). Both halves are fixed: the listener below drives this, and
 * the outcome is now reported either way. A notification the shopper believes is set but is not is
 * worse than a visible failure.
 *
 * Returns the jqXHR so a caller - or a test - can wait for the outcome.
 */
export function stockNotify(userid, aaia, pn, add) {
    return $.ajax({
        // No leading slash: layout.inc emits <base href="<?= base_url() ?>">, and an
        // absolute path resolves from the origin root instead, ignoring it. Harmless on a
        // domain-root install - which every configured baseURL is today - but the relative form
        // is both correct and the majority convention here (19 relative to 8 absolute). Raised in
        // review on #965; the other 7 absolute call sites are untouched and pre-existing.
        url: 'catalog/ajaxAddStockNotification',
        type: 'post',
        data: {
            aaia: aaia,
            pn: pn,
            userid: userid,
            add: add ? 1 : 0,
        },
        success: function () {
            displayToastrAlert(
                'success',
                add
                    ? 'We will email you when this item is back in stock.'
                    : 'You will no longer be notified about this item.'
            );
        },
        error: function () {
            console.error('Failed to submit stock notification request');
            displayToastrAlert(
                'error',
                'Sorry - that could not be saved. Please try again.'
            );
        },
    });
}

// Module scope, NOT inside readyProductBrowsePage(). That function early-returns unless one of
// nine has* flags matches something on the page, and whether the product page happens to satisfy
// one of them is no business of the stock-notify checkbox. Delegated from document so it also
// covers a checkbox rendered after load.
$(document).on('change', '[data-stock-notify]', function () {
    var $box = $(this);

    stockNotify(
        $box.data('stock-notify-user'),
        $box.data('stock-notify-aaia'),
        $box.data('stock-notify-pn'),
        $box.prop('checked')
    );
});

$(document).ready(readyProductBrowsePage);
